"use client";

import { useCallback, useEffect } from "react";
import { useFingerprintDecision } from "@/hooks/useFingerprintDecision";
import GetAppWithLinkCopy from "@/components/ios-copy-link/GetAppWithLinkCopy";
import { ClientRedirectProps } from "./ClientRedirect.types";
import { RedirectLayout } from "./RedirectLayout";
import { AppPreviewInterstitial } from "./AppPreviewInterstitial";
import { RedirectHtmlFrame } from "./RedirectHtmlFrame";
import { RedirectSpinner } from "./RedirectSpinner";
import { RedirectStatusContent } from "./RedirectStatusContent";
import { getAppDisplay } from "@/lib/utils/redirectFlow/appDisplay";
import {
  buildPlayStoreUrl,
  getSearchParamsFromPath,
} from "@/lib/utils/linkUtils";
import { useClickReporter } from "@/hooks/redirectFlow/useClickReporter";
import { useRedirectFlow } from "@/hooks/redirectFlow/useRedirectFlow";
import { useWebViewAppOpen } from "@/lib/webViewAppOpen/useWebViewAppOpen";
import { selectRedirectScreen } from "@/lib/utils/redirectFlow/redirectScreen";
import { useAppBounce } from "@/lib/inAppEscape/InAppEscapeContext";

function ClientRedirect({
  originalPath,
  deferredLink,
  appConfig,
  enableCopyLink,
  pasteboardToken,
  redirectionUrls,
  serverOs,
  isBotRequest,
  appPreviewDocument,
  triesAppOpen,
  shortLinkUrl,
}: ClientRedirectProps) {
  const { shouldSendFingerprintImmediately, os } =
    useFingerprintDecision(enableCopyLink);
  // See useAppBounce for what a tap does on App Preview and copy-link here.
  const appBounce = useAppBounce();
  const clickPasteboardToken =
    appBounce?.bouncedPasteboardToken ?? pasteboardToken;

  const { createClick, clickId } = useClickReporter({
    originalPath,
    deferredLink,
    pasteboardToken: clickPasteboardToken,
    shortLinkUrl,
    isBotRequest,
  });

  // The server builds the Play Store URL before the click exists, so the URL has
  // no click id. It is built again once the click is saved: the app needs the id
  // to match the install exactly.
  const playStoreUrlWithClickId = useCallback(
    (id: string) =>
      buildPlayStoreUrl(appConfig, id, getSearchParamsFromPath(originalPath)),
    [appConfig, originalPath]
  );
  const redirectionUrlsWithClickId = clickId
    ? { ...redirectionUrls, android: playStoreUrlWithClickId(clickId) }
    : redirectionUrls;

  const appOpen = useWebViewAppOpen({
    packageName: appConfig?.package_name,
    createClick,
    playStoreUrlWithClickId,
  });

  const { redirectionUrl, redirectStatus, attemptRedirect } = useRedirectFlow({
    os,
    serverOs,
    deferredLink,
    redirectionUrls,
    createClick,
    playStoreUrlWithClickId,
  });

  const { appIconSrc, appName } = getAppDisplay(
    (os ?? serverOs) as "ios" | "android" | "web",
    appConfig
  );

  // The interstitial sits between a visitor and an app store, so it only makes
  // sense on mobile. `appPreviewDocument` already carries the server's read of
  // the same conditions; this re-checks them against the OS the client actually
  // detected, which is the one the redirect itself is resolved from.
  const showAppPreview =
    shouldSendFingerprintImmediately === true &&
    (os === "ios" || os === "android") &&
    !isBotRequest &&
    !!appPreviewDocument &&
    !appBounce?.returnedFromBounce;

  // Immediate redirect flow: auto-redirect, unless an App Preview interstitial
  // is configured — then wait for the user to tap its CTA instead.
  useEffect(() => {
    if (!shouldSendFingerprintImmediately || !redirectionUrl) return;

    // No auto-redirect: the visitor picks the app or the store. See
    // lib/webViewAppOpen.
    if (appOpen) return;

    if (showAppPreview) {
      // Deliberately no already-redirected check here, unlike the auto-redirect
      // flow below. That check exists to stop an automatic redirect from
      // looping, and nothing on this path is automatic — the visitor has to tap
      // Continue.
      //
      // Record the click on load, exactly as the auto-redirect flow does, so
      // "click" means the same thing whether or not an interstitial is
      // configured and a visitor who bounces here is still attributable.
      // Nothing waits on this request: the click id reaches /api/link/continue
      // via the cookie its response sets, so the interstitial paints
      // immediately. The Continue tap is a separate engagement signal recorded
      // by /api/link/continue.
      //
      // Not where Continue bounces to the app first. See useAppBounce.
      if (!appBounce) createClick(true);
      return;
    }

    attemptRedirect(redirectionUrl);
  }, [
    shouldSendFingerprintImmediately,
    redirectionUrl,
    appOpen,
    showAppPreview,
    appBounce,
    createClick,
    attemptRedirect,
  ]);

  // iOS copy-link flow: create click on mount, then show interactive UI
  useEffect(() => {
    if (shouldSendFingerprintImmediately !== false || isBotRequest) return;

    // The store tap records the click instead. See lib/webViewAppOpen.
    if (appOpen) return;

    // The first tap bounces to the app. See useAppBounce.
    if (appBounce) {
      if (appBounce.returnedFromBounce && redirectionUrl) {
        attemptRedirect(redirectionUrl);
      }
      return;
    }

    createClick();
  }, [
    shouldSendFingerprintImmediately,
    isBotRequest,
    appOpen,
    appBounce,
    redirectionUrl,
    createClick,
    attemptRedirect,
  ]);

  const isCopyLinkFlow = (os ?? serverOs) === "ios" && enableCopyLink;
  const customHtml = isCopyLinkFlow
    ? appConfig?.custom_copy_link_html
    : appConfig?.custom_redirect_html;

  const screen = selectRedirectScreen({
    os,
    shouldSendFingerprintImmediately,
    // An empty redirect_url resolves to "" rather than undefined, so it never
    // becomes a "failed" status — there is nowhere to send this visitor and
    // nothing to report, so keep waiting.
    hasNowhereToGo: !redirectionUrl && !redirectStatus,
    appPreviewDocument,
    showAppPreview,
    customHtml,
    triesAppOpen,
    attemptsAppOpen: !!appOpen,
    redirectStatus,
  });

  switch (screen.kind) {
    case "appPreview":
      return <AppPreviewInterstitial html={screen.html} />;
    case "customHtml":
      return <RedirectHtmlFrame html={screen.html} />;
    case "spinner":
      return (
        <RedirectLayout appIconSrc={appIconSrc} appName={appName}>
          <RedirectSpinner />
        </RedirectLayout>
      );
    case "appOpenAttempted":
      return (
        <RedirectLayout appIconSrc={appIconSrc} appName={appName}>
          <RedirectStatusContent
            status="appOpenAttempted"
            os={screen.os}
            redirectionUrls={redirectionUrlsWithClickId}
            onStoreTap={appOpen?.onStoreTap}
          />
        </RedirectLayout>
      );
    case "copyLink":
      return (
        <GetAppWithLinkCopy
          redirectionUrl={redirectionUrl}
          pasteboardToken={os === "ios" ? clickPasteboardToken : null}
          appIconSrc={appIconSrc}
          appName={appName}
          customHtml={customHtml}
          darkTheme={appConfig?.copy_link_dialog_dark_theme ?? false}
          language={appConfig?.copy_link_dialog_language}
        />
      );
    case "status":
      return (
        <RedirectLayout appIconSrc={appIconSrc} appName={appName}>
          <RedirectStatusContent
            status={redirectStatus}
            os={screen.os}
            redirectionUrls={redirectionUrlsWithClickId}
          />
        </RedirectLayout>
      );
  }
}

export default ClientRedirect;
